import { Box, Button, Content, Flex, StrokeIcon, useIsSticky } from '@acsbe/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { StripeLoader, useStripeModule } from '../../../../../context/stripe';
import axios from '../../../../../utils/axiosInstance';

import { useDomainSelection } from '../../../../../context';
import { useRefreshUserData } from '../../../../../hooks/useRefreshUserData';
import { type StripeData } from '../StripeWizard';
import { type WizardStepComponent } from '../Wizard';

const googlePlacesApiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

export const AddressStep: WizardStepComponent<StripeData> = ({
  data,
  setData,
  next,
}) => {
  const { AddressElement, useStripe, useElements } = useStripeModule();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [addressReady, setAddressReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addressError, setAddressError] = useState<null | string | undefined>(
    null,
  );
  const { isSticking, stickyElementRef } =
    useIsSticky<HTMLDivElement>('bottom');
  const [country, setCountry] = useState<string | undefined>();
  const { currentAccessibeAccount, setCurrentAccessibeAccount } =
    useDomainSelection();
  const { refreshUserData } = useRefreshUserData();

  useEffect(() => {
    const getLocation = async () => {
      try {
        const response = await axios.get('/api/stripe/get-location', {
          withCredentials: true,
        });
        setCountry(response.data.data?.geolocation?.country);
      } catch (error) {
        setCountry('US');
      }
    };
    getLocation();
  }, []);

  const handleContinue = async () => {
    if (!elements || isLoading) return;

    setIsLoading(true);
    setAddressError(null);
    try {
      const addressElement = elements.getElement('address');
      if (addressElement) {
        const { complete, value } = await addressElement.getValue();
        if (complete) {
          // Validate address with Google to ensure it's correct
          const validation = await axios
            .post('/api/stripe/validate-address', value, {
              withCredentials: true,
            })
            .then((res) => res.data)
            .catch(() => ({
              isValid: false,
              error: 'Unable to validate address. Please try again.',
            }));

          if (!validation.isValid) {
            setAddressError(validation.error);
            return;
          }

          setData({
            ...data,
            address: value,
          });

          const success = await axios
            .post('/api/stripe/create-customer', value, {
              withCredentials: true,
            })
            .then(() => true)
            .catch(() => {
              return false;
            });

          if (!success) {
            setAddressError('Something went wrong. Please try again.');
            return;
          }

          const userData = await refreshUserData();
          if (userData) {
            setCurrentAccessibeAccount(userData.account);
          }

          next();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!stripe) return null;

  return (
    <Flex gap="lg" radius="xl" padding={6} style={{ position: 'relative' }}>
      {!addressReady && <StripeLoader />}

      <Box>
        {addressReady && (
          <Box paddingBlockEnd={6}>
            <Content>{t('STRIPE.ADDRESS_STEP_DESCRIPTION')}</Content>
          </Box>
        )}

        <Box
          style={{
            pointerEvents: addressReady ? 'auto' : 'none',
            opacity: addressReady ? 1 : 0,
          }}
        >
          <AddressElement
            onChange={() => setAddressError(null)}
            onReady={() => setAddressReady(true)}
            options={{
              mode: 'billing',
              display: { name: 'organization' },
              autocomplete: {
                mode: 'google_maps_api',
                apiKey: googlePlacesApiKey,
              },
              defaultValues: {
                name: data.address?.name || currentAccessibeAccount?.name,
                address: {
                  line1: data.address?.address.line1,
                  line2: data.address?.address.line2 ?? null,
                  city: data.address?.address.city,
                  state: data.address?.address.state,
                  postal_code: data.address?.address.postal_code,
                  country: data.address?.address.country || country || 'US',
                },
              },
            }}
          />
          {addressError && (
            <Box paddingBlockStart={4}>
              <Content scale="danger">{addressError}</Content>
            </Box>
          )}
        </Box>
      </Box>

      {addressReady && (
        <Footer
          data-sticky={isSticking}
          gap={isSticking ? 4 : 6}
          {...(!isSticking && { marginBlockEnd: -2 })}
          marginBlockStart={8}
          position="sticky"
          ref={stickyElementRef}
        >
          <Button
            aria-busy={isLoading}
            data-mixpanel="add-payment-method-dialog-address-continue-button"
            disabled={isLoading}
            onClick={handleContinue}
            scale="primary"
            type="button"
          >
            {t('CONTINUE')}
            <StrokeIcon name="ChevronRight" />
          </Button>
        </Footer>
      )}
    </Flex>
  );
};

const Footer = styled(Flex)`
  inset-block-end: calc(0px - var(--padding));

  &[data-sticky='true'] {
    background-color: ${({ theme }) =>
      theme.palette.scales.base.background.app};
    border-top: 1px solid
      ${({ theme }) => theme.palette.scales.base.border.static};
    box-shadow: ${({ theme }) => theme.shadows[3]};
    margin-inline: calc(0px - var(--padding));
    margin-block-end: calc(0px - var(--padding));
    padding: var(--padding);
  }
`;
